import { Component, computed, inject, model, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { injectQueryParams } from "ngxtension/inject-query-params";
import { ProductType } from "@malirang/common";
import {
    AlertService,
    Align,
    Button,
    ColumnDefinition,
    GridOptions,
    ModalService,
    Segment,
    SegmentOption,
    ServerSidePaginationDataGrid,
    ToastService,
} from "@malirang/front-share";
import { rxResource } from "@angular/core/rxjs-interop";
import { ProductDto, ProductService } from "@malirang/api-client";
import { Router, RouterLink } from "@angular/router";
import { ProductTitleComponent } from "./components/product-title/product-title.component";
import { PinComponent } from "../../../components/pin/pin.component";
import { ProductPriceComponent } from "./components/product-price/product-price.component";
import { injectParams } from "ngxtension/inject-params";
import {
    ProductCategoryModal,
    ProductCategoryModalProps,
} from "./category/product-category.modal";

export const ProductColumns = [
    {
        type: "custom",
        field: "isPinned",
        name: "고정",
        renderItem: PinComponent,
    },
    {
        type: "boolean",
        field: "isExposed",
        name: "노출",
        badgeProps: {
            true: { text: "노출", color: "green" },
            false: { text: "숨김", color: "gray" },
        },
    },
    {
        type: "text",
        field: "category.name",
        name: "카테고리",
        formatter: (value: string) => value || "-",
    },
    {
        type: "custom",
        field: "name",
        name: "상품명",
        renderItem: ProductTitleComponent,
    },
    {
        type: "custom",
        field: "price",
        name: "판매가",
        renderItem: ProductPriceComponent,
    },
    {
        type: "number",
        field: "viewCount",
        name: "조회",
    },
    {
        type: "number",
        field: "purchaseCount",
        name: "구매",
    },
    {
        type: "date",
        field: "createdAt",
        name: "등록일시",
    },
] as const;

export const ProductTypeParams = {
    디지털자료: "digital",
    교구: "tool",
    교재: "book",
} as const;

@Component({
    selector: "app-product",
    templateUrl: "./product.page.html",
    styleUrls: ["./product.page.css"],
    imports: [
        CommonModule,
        ServerSidePaginationDataGrid,
        Segment,
        Button,
        RouterLink,
    ],
})
export default class ProductPage {
    readonly productService = inject(ProductService);
    readonly modalService = inject(ModalService);
    readonly router = inject(Router);
    readonly alertService = inject(AlertService);
    readonly toastService = inject(ToastService);

    $res = rxResource({
        stream: ({ params }) =>
            this.productService.productControllerSearch({
                ...params,
            }),
        params: () => ({
            pageNo: this.pageNo(),
            pageSize: this.pageSize(),
            align: this.align(),
            orderBy: this.orderBy(),
            query: this.query(),
            isExposed:
                this.segment() === "all"
                    ? undefined
                    : this.segment() === "true",
            type: this.type(),
        }),
    });

    items = computed(() => this.$res.value()?.items || []);
    pageInfo = computed(() => this.$res.value()?.pageInfo);

    cols: ColumnDefinition[] = [...ProductColumns];

    options: GridOptions = {
        view: "table",
        rowNumber: true,
        badge: {
            variant: "outline",
        },
        row: {
            clickHandler: (row: ProductDto) => {
                this.router.navigate(["/store/detail", row.id]);
            },
            options: [
                {
                    label: "수정",
                    handler: (row: ProductDto) => {
                        this.router.navigate(
                            [`/store/form/${ProductTypeParams[row.type]}`],
                            {
                                queryParams: {
                                    id: row.id,
                                },
                            },
                        );
                    },
                },
                {
                    label: "삭제",
                    handler: (row: ProductDto) => {
                        this.alertService
                            .warning("상품을 삭제하시겠습니까?")
                            .closed.subscribe({
                                next: (res) => {
                                    if (res?.action === "confirm") {
                                        this.productService
                                            .productControllerDelete({
                                                id: row.id,
                                            })
                                            .subscribe({
                                                next: () => {
                                                    this.toastService.success(
                                                        "상품이 삭제되었습니다.",
                                                    );
                                                    this.$res.reload();
                                                },
                                            });
                                    }
                                },
                            });
                    },
                },
                {
                    label: "노출/숨김",
                    handler: (row: ProductDto) => {
                        this.alertService
                            .warning(
                                `상품을 ${row.isExposed ? "숨김" : "노출"} 처리하시겠습니까?`,
                            )
                            .closed.subscribe({
                                next: (res) => {
                                    if (res?.action === "confirm") {
                                        this.productService
                                            .productControllerExpose({
                                                id: row.id,
                                                isExposed: !row.isExposed,
                                            })
                                            .subscribe({
                                                next: () => {
                                                    this.toastService.success(
                                                        `상품이 ${row.isExposed ? "숨김" : "노출"} 처리되었습니다.`,
                                                    );
                                                    this.$res.reload();
                                                },
                                            });
                                    }
                                },
                            });
                    },
                },
            ],
        },
    };

    pageNo = model(1);
    pageSize = model(10);
    align = model<Align>("desc");
    orderBy = model("createdAt");
    query = model("");

    segment = model("all");

    segments: SegmentOption[] = [
        { label: "전체", value: "all" },
        { label: "노출", value: "true" },
        { label: "비노출", value: "false" },
    ];

    _type = injectParams("type");
    type = computed(() => {
        switch (this._type()) {
            case "digital":
                return ProductType.디지털자료;
            case "tool":
                return ProductType.교구;
            case "book":
                return ProductType.교재;
            default:
                return ProductType.디지털자료;
        }
    });

    handleManageCategory() {
        this.modalService.create<ProductCategoryModalProps>(
            ProductCategoryModal,
            {
                componentProps: {
                    type: this.type(),
                },
            },
        );
    }
}
